import { useState } from 'react';
import { useFormatter, useTranslations } from 'use-intl';
import Tooltip from '@/presentation/components/ui/Tooltip';
import Collapsible from '@/presentation/components/modules/Collapsible';
import { SwapPrice } from '@/core/swap';
import Price from '@/presentation/components/elements/Price';
import FormattedEther from '@/presentation/components/elements/FormattedEther';

const SwapSummary = ({
  quote,
  sellAmount,
  buyAmount,
  sellSymbol,
  buySymbol,
  slippage,
  gasPrice,
}: {
  quote?: SwapPrice | null;
  sellAmount: number;
  buyAmount: number;
  sellSymbol: string;
  buySymbol: string;
  slippage: string;
  gasPrice: bigint;
}) => {
  const t = useTranslations('Swap.Summary');
  const { number } = useFormatter();
  const [ratio, setRatio] = useState<'buy' | 'sell'>('buy');

  const exchangeRate = number(
    ratio === 'buy' ? sellAmount / buyAmount : buyAmount / sellAmount
  );

  const gasEstimateEth = gasPrice * (quote?.estimatedGas ?? 0n);
  const priceImpact = quote?.priceImpact;

  return (
    <Collapsible
      header={
        <span
          className="font-bold"
          onClick={(e) => {
            e.stopPropagation();
            setRatio((r) => (r === 'buy' ? 'sell' : 'buy'));
          }}
        >
          {ratio === 'buy'
            ? `1 ${buySymbol} = ${exchangeRate} ${sellSymbol}`
            : `1 ${sellSymbol} = ${exchangeRate} ${buySymbol}`}
        </span>
      }
    >
      <dl className="space-y-1 p-6">
        <div className="flex items-baseline space-x-3">
          <dt>{t(`list.slippage`)}</dt>
          <span className="flex-1 border-b border-dotted border-gray-300 h-0"></span>
          <dd className="text-right whitespace-nowrap truncate">
            {number(Number(slippage), {
              style: 'percent',
              minimumFractionDigits: 2,
            })}
          </dd>
        </div>
        <div className="flex items-baseline space-x-3">
          <dt>{t(`list.impact`)}</dt>
          <span className="flex-1 border-b border-dotted border-gray-300 h-0"></span>
          <dd className="text-right whitespace-nowrap truncate">
            {priceImpact
              ? number(priceImpact, {
                  style: 'percent',
                  minimumFractionDigits: 2,
                })
              : '-'}
          </dd>
        </div>
        {!!quote?.route && (
          <div className="flex items-baseline space-x-3">
            <dt>{t(`list.route` as any)}</dt>
            <span className="flex-1 border-b border-dotted border-gray-300 h-0"></span>
            <dd className="text-right whitespace-nowrap truncate">
              <Tooltip
                label={
                  <div className="flex gap-1">
                    {quote?.route.map((r, i) => {
                      return (
                        <div key={i} className="flex space-x-1">
                          <span>
                            {r.path.map((x, j, k) => (
                              <span key={j}>
                                {`${x.tokenOut.symbol} > ${
                                  x.tokenIn.symbol
                                } (${number(Number(r.proportion), {
                                  style: 'percent',
                                })})`}
                                {j < k.length - 1 ? ' >> ' : null}
                              </span>
                            ))}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                }
                contentClassName="max-w-prose overflow-hidden"
                content={
                  <div className="whitespace-break-spaces">
                    {quote.routeString}
                  </div>
                }
              />
            </dd>
          </div>
        )}
        <div className="h-4" />
        {quote?.type === 'mint' ||
        quote?.type === 'redeem' ||
        quote?.type === 'erc20' ? (
          <div className="flex items-baseline space-x-3">
            <dt>{t(`list.amount`)}</dt>
            <span className="flex-1 border-b border-dotted border-gray-300 h-0"></span>
            <dd className="text-right whitespace-nowrap truncate">
              {(quote.type === 'mint' || quote.type === 'redeem') && (
                <>
                  <FormattedEther value={quote.amount} />{' '}
                  {quote.type === 'mint' ? buySymbol : sellSymbol}
                </>
              )}
              {quote.type === 'erc20' && <Price value={quote.amount} />}
            </dd>
          </div>
        ) : quote?.type === 'swap' ? null : (
          <div className="flex items-baseline space-x-3">
            <dt>{t(`list.amount`)}</dt>
            <span className="flex-1 border-b border-dotted border-gray-300 h-0"></span>
            <dd className="text-right whitespace-nowrap truncate">
              <Price value={quote?.vTokenPrice || 0n} />
            </dd>
          </div>
        )}
        {!!quote?.premiumPrice && (
          <div className="flex items-baseline space-x-3">
            <dt>{t(`list.premium`)}</dt>
            <span className="flex-1 border-b border-dotted border-gray-300 h-0"></span>
            <dd className="text-right whitespace-nowrap truncate">
              <Price value={quote.premiumPrice} />
            </dd>
          </div>
        )}
        {!!quote?.feePrice && (
          <div className="flex items-baseline space-x-3">
            <dt>{t(`list.fee`)}</dt>
            <span className="flex-1 border-b border-dotted border-gray-300 h-0"></span>
            <dd className="text-right whitespace-nowrap truncate">
              <Price value={quote.feePrice} />
            </dd>
          </div>
        )}
        {!!(gasEstimateEth >= 100000000000000n) && (
          <div className="flex items-baseline space-x-3 font-light">
            <dt>Estimated gas</dt>
            <span className="flex-1 border-b border-dotted border-gray-300 h-0"></span>
            <dd className="text-right whitespace-nowrap truncate">
              {'('}
              <Price value={gasEstimateEth} maximumFractionDigits={4} />
              {')'}
            </dd>
          </div>
        )}
        {!!quote?.price &&
          (quote.type === 'erc20' ||
            quote.type === 'buy' ||
            quote.type === 'sell' ||
            quote.type === 'swap') && (
            <div className="flex items-baseline space-x-3 font-bold">
              <dt>{t(`list.total`)}</dt>
              <span className="flex-1 border-b border-dotted border-gray-300 h-0"></span>
              <dd className="text-right whitespace-nowrap truncate">
                <Price value={quote.price} />
              </dd>
            </div>
          )}
      </dl>
    </Collapsible>
  );
};

export default SwapSummary;
